import { memo } from "react";
import { FieldType, Option } from "../../../../../../../../model/service-request/attribute";
import { AttributeValue } from "../../../../../../../../model/service-request/attribute-value";
import styles from "./RenderAttribute.module.scss"
import { formatTimestamp } from "../../../../../../../../utils";
import { ReactComponent as DownloadIcon } from '../../../../../../../../assets/icons/download.svg';

type Props = {
    attributeValue: AttributeValue;
};

const RenderAttribute: React.FC<Props> = memo((props: Props) => {
    const attributeValue = props.attributeValue;
    const attributeDefinition = attributeValue.attributeDefinition;

    switch (attributeValue.attributeDefinition.fieldType) {
        case FieldType.Text:
            return (
                <div className={styles.informationContainer}>
                    <span>{attributeDefinition.label}</span>
                    <span>{attributeValue.stringValue}</span>
                </div>
            );

        case FieldType.TextDescription:
            return (
                <div className={styles.informationContainer}>
                    <span>{attributeDefinition.label}</span>
                    <div className={styles.textDescriptionContainer}>
                        <span>{attributeValue.stringValue}</span>
                    </div>
                </div>
            );

        case FieldType.Number:
            return (
                <div className={styles.informationContainer}>
                    <span>{attributeDefinition.label}</span>
                    <span>{attributeValue.doubleValue}</span>
                </div>
            );

        case FieldType.Bool:
            return (
                <div className={styles.informationContainer}>
                    <span>{attributeDefinition.label}</span>
                    <span>{attributeValue.boolValue ? "True" : "False"}</span>
                </div>
            );

        case FieldType.ConditionalDropdown:
            const parentOptions = attributeDefinition.childrenOptionDefinitions || [];

            const childParents = parentOptions.map((parent) => {
                const selectedOptions = attributeValue.selectedOptionIds || [];
                const childOption = parent.childOptions?.find(child => selectedOptions.includes(child.id!));

                return {
                    parentValue: parent.value,
                    childValue: childOption?.value ?? "",
                    childPrice: childOption?.price
                };
            });

            return (
                <div className={styles.informationContainer}>
                    <span>{attributeDefinition.label}</span>
                    <div>
                        {childParents.map((cp, index) => (
                            <span key={index}>
                                {cp.parentValue}({cp.childValue}{cp.childPrice && ` - ${cp.childPrice.currency} ${cp.childPrice.amount}`})
                                {index < childParents.length - 1 ? ", " : ""}
                            </span>
                        ))}
                    </div>
                </div>
            );

        case FieldType.Dropdown:
            const selectedOptions = (attributeValue.selectedOptionIds || [])
                .map(selectedOptionId => attributeDefinition.childrenOptionDefinitions?.find(optionDefinition => optionDefinition.id === selectedOptionId))
                .filter((value): value is Option => value !== undefined);

            return (
                <div className={styles.informationContainer}>
                    <span>{attributeDefinition.label}</span>
                    <div>
                        {selectedOptions.map((so, index) => (
                            <span key={index}>
                                {so.value}{so.price && `(${so.price.currency} ${so.price.amount})`}
                                {index < selectedOptions.length - 1 ? ", " : ""}
                            </span>
                        ))}
                    </div>
                </div>
            );

        case FieldType.DateTime:
            return (
                <div className={styles.informationContainer}>
                    <span>{attributeDefinition.label}</span>
                    <span>{formatTimestamp(attributeValue.dateValue)}</span>
                </div>
            );

        case FieldType.File:
        case FieldType.Image:
            return (
                <div className={styles.informationContainer}>
                    <span>{attributeDefinition.label}</span>
                    <div className={styles.fileContainer}>
                        <a href={attributeValue.urlValue}>Download file</a>
                        <DownloadIcon className={styles.downloadIcon} />
                    </div>
                </div>
            );

        default:
            return null;
    }
});

export default RenderAttribute;